import type { AgentAction, ReadOnlyListCard } from '@rochadenis/axiomgo-contracts'
import { aguardarPasseComPrazo, CREDENTIAL_WAIT_TIMEOUT_MS } from './credential-wait'
import { handleUnauthorized, markSessionHealthy } from './auth-recovery'
export { aguardarPasseComPrazo, CREDENTIAL_WAIT_TIMEOUT_MS } from './credential-wait'
/**
 * SSE streaming client for chat messages.
 * Replaces WS fire-and-forget for chat to prevent frontend timeouts
 * during long agent processing (20-60s).
 *
 * The WS connection remains active for ping/status/notifications.
 */
import * as Sentry from '@sentry/react'
import { estadoInicialDasMarcas, marcaDaTela, marcaEventoRecebido } from './marcas-do-turno'
import { useAuthStore } from '../shared/stores/auth'
import { getEngineRestUrl } from './config'
import { currentTurnLocale, currentTurnLocaleExplicit } from './turn-locale'
import { displayForServerError } from './chat-stream-error'
import type { FileRef, OutboundAttachment } from './engine/protocol'
import type { VoiceConfirmationRequired } from './voice/confirmation-handshake'
import type {
  EquipmentRegistrationActionPayload,
  ExecutionConfirmationEnvelope,
  EntityCard,
  PreventivePlanEntryCard,
  GuidanceCard,
  PlanDetailCardEnvelope,
  WorkOrderDetailCardEnvelope,
  EquipmentSearchResult,
  EquipmentSearchFormCardData,
  EquipmentSearchFormActionPayload,
  TaskMaterialsActionPayload,
  TaskMaterialsCardData,
  PlanEditTableCard,
  PlanEditActionPayload,
  RailActionDispatchPayload,
  OnboardingRailStepEnvelope,
  TeamManualActionPayload,
  MaterialsManualActionPayload,
  ResourceAllocationActionPayload,
  ImportFlowActionWire,
  ImportFlowSnapshot,
  CorrectiveAction,
} from '../types/rich-content'
import type { NotificationAction } from './chat/clickedActionPresentation'
import {
  ColetorDeArtifacts,
  type ArtifactPronto,
  type CabecalhoDeArtifact,
  type FalhaDeArtifact,
} from '../features/artifacts/artifact-stream'
import type { Sugestao } from '../stores/sugestoesStore'

/**
 * Trilho finalização 2026-07-15 follow-up (SPEC §4.3, one channel): the wire
 * envelope for `onboardingAction` accepts:
 *  - the plan-edit table shape (`PlanEditActionPayload` — `{ actionId, payload }`),
 *  - the pure RAIL-click shape (`RailActionDispatchPayload` — `{ actionId }`)
 *    used by every trilho-owned button (Continuar, escolha de função, escolha
 *    de segmento, o kickoff `onboarding.kickoff`, etc.) so a click never
 *    regresses to `content: value` text.
 *  - RAIZ 1, fatias 2/3 (#1037/#1039) — the team-roster / materials-inventory
 *    manual-entry table shapes (`TeamManualActionPayload` /
 *    `MaterialsManualActionPayload`), same one-channel discipline: the
 *    engine's edge parser reads `payload.technicianRows` / `.materialRows`
 *    off this SAME `onboardingAction.payload`, never a parallel field.
 * All are read by the engine's edge parser (`parseOnboardingActionPayload`)
 * and routed by the deterministic dispatcher (`dispatchOnboardingRailTurn`);
 * there is NO parallel `planEditAction`/`railAction` channel.
 */
export type OnboardingActionWire =
  | PlanEditActionPayload
  | RailActionDispatchPayload
  | TeamManualActionPayload
  | MaterialsManualActionPayload
  // Passo 8 (alocação). Estava FORA desta união até 03/08/2026 — e como
  // `RailActionDispatchPayload` é `{ actionId: string }`, todo payload de
  // alocação entrava no fio por ali, com o resto do conteúdo indo para o lixo
  // sem uma linha vermelha (a checagem de propriedade extra do TypeScript não
  // vale para variável). Estar na união é o que põe esta tela sob o portão
  // `types/onboardingActionWire.contract.test.ts`.
  | ResourceAllocationActionPayload
  // Cards de 2026-08-03/04. Entram na união pelo MESMO motivo que a alocação
  // entrou em 03/08: fora dela, um objeto com `actionId` passa pelo curinga
  // `RailActionDispatchPayload` e o resto do conteúdo vai para o lixo sem uma
  // linha vermelha. Os dois já declaram a fatia `payload` — estar aqui é o que
  // os põe sob `types/onboardingActionWire.contract.test.ts` e impede que alguém
  // mova um campo para o topo depois, quebrando em silêncio.
  | EquipmentSearchFormActionPayload
  | TaskMaterialsActionPayload
import type { PreventivePlanAction, PreventivePlanDraftRichContent } from '../types/preventive-plan'
import type { NpsAction } from '../types/nps-pulse'
import type { PlanComparisonAction } from '../types/plan-comparison'
import type { PhotoPolicyAction } from '../types/rich-content'
import type { ToolOnlyEquipmentCard } from '../types/web-search'
import type { DashboardCardEnvelope } from './kpi/types'
import type { PlansCardEnvelope } from '../features/maintenance-plans/types'
import type { LtpScheduleEnvelope, LtpEquipmentPickerEnvelope } from '../types/ltp-schedule'

/**
 * Entrega a falha do coletor, se houver. Mora AQUI, e nao dentro do `case`, por
 * causa do portao de modularidade: um bloco `{ }` no `case` empurraria o
 * `switch` — que ja esta em profundidade 5 — para 7, e o `lint:strict` conta
 * isso como violacao nova. Extrair e o conserto; alargar o teto seria calar.
 */
function entregaFalhaDePainel(callbacks: SSECallbacks, falha: FalhaDeArtifact | null): void {
  if (falha) callbacks.onArtifactFalhou?.(falha)
}

/** Idem: entrega o painel pronto, ou a falha de fechamento. */
function entregaPainelFechado(
  callbacks: SSECallbacks,
  fim: { ok: true; artifact: ArtifactPronto } | { ok: false; falha: FalhaDeArtifact },
): void {
  if (fim.ok) callbacks.onArtifact?.(fim.artifact)
  else callbacks.onArtifactFalhou?.(fim.falha)
}

export interface SSECallbacks {
  onProcessing: () => void
  /** `data` é o corpo livre do frame (ex.: grupo de tarefas de `plan_tasks_partial`). */
  onProgress: (step: string, detail: string, data?: unknown) => void
  onChunk: (content: string) => void
  onResponse: (data: {
    conversationId: string
    content: string
    /** ADR-307 — engine echoes the resolved tenantId on the SSE response top-level so
     *  connectionStore.isCrossTenant() can match frames (read via cast in the handler). */
    tenantId?: string
    toolsUsed?: string[]
    /**
     * O rastro do turno (motor >= 0.71.0): nome, parametro redigido e contagem
     * do resultado, um por ferramenta. OPCIONAL de proposito — motor mais velho
     * nao manda, e a tela recua para `toolsUsed` (so nomes) sem quebrar.
     */
    toolTrace?: Array<{
      name: string
      params?: Record<string, unknown>
      summary?: string
      state?: 'output-available' | 'output-error'
    }>
    model?: string
    webSearch?: {
      searched: boolean
      provider: string
      source_count: number
      cache_hit: boolean
      sources: Array<{ title: string; url: string; confidence: number; domain: string }>
      equipment: { manufacturer: string; model: string } | null
    }
    suggested_actions?: Array<{ label: string; value: string }>
    /**
     * Bug-fiação 2026-07-12 — the engine ALWAYS emits `suggestedActions`
     * (camelCase) on the SSE response payload; `suggested_actions` (snake_case)
     * above is a stale/never-sent shape from commit a28c6529 (2026-04-21).
     * Declared here so connectionStore can read the camelCase field the
     * engine actually sends, falling back to the snake_case one for safety.
     *
     * Trilho finalização 2026-07-15 — `actionId` is declared because the engine
     * DOES send it: a rail-owned turn pre-seeds `suggestedActions` with the
     * step's `RailAction[]` (`critic.ts:556` → `suggestedActions = [...railOwnedActions]`,
     * surfaced by `ConversationOrchestrator.ts:1292`). It was already reaching
     * MessageBubble at runtime (connectionStore casts the raw array to
     * `SuggestedAction[]` rather than mapping `{label,value}`), so declaring it
     * changes no behaviour — it stops the live rail field from riding the wire
     * undeclared, where a future explicit field map would silently drop it and
     * regress every rail click back to free text.
     */
    suggestedActions?: Array<{
      label: string
      value: string
      actionId?: string
      /**
       * Motor PR #1412 — ação estruturada opcional (espelha
       * `SuggestedAction.structuredAction`). Declarada aqui para o cast do
       * `connectionStore` ser honesto: o campo trafega no envelope e é despachado
       * como `preventivePlanAction` no clique (MessageBubble), não como texto.
       */
      structuredAction?: {
        preventivePlanAction?: { kind: 'start' | 'start_manual'; equipmentId?: string }
        agentAction?: import('@rochadenis/axiomgo-contracts').AgentAction
      }
    }>
    /**
     * Sprint 1A NEW1 — 3 cards propositivos emitidos pelo engine no
     * top-level do payload SSE (judge ACTION_CARDS valida via Haiku +
     * retry + fallback genérico). Passed through to
     * chatStore.Message.metadata.actionCards so MessageBubble can render
     * ActionCardList. The original PR #210 fix only patched the WS branch
     * of connectionStore — chat unified transport to SSE in ADR-004
     * phase 1, so the SSE handler is the live code path.
     */
    actionCards?: Array<{
      label: string
      category: 'strategic' | 'alternative' | 'pointwise'
      standard_message: string
      next_wow: string | null
    }>
    /**
     * SDD-001 Phase 5c — structured card envelope emitted when the turn
     * produced a card-worthy tool result (analyze_spreadsheet +
     * confirm_import_proposal for now). Passed straight through to
     * chatStore.Message.richContent so useMessageParsing can render the
     * ImportProposalCard / ImportProgressBar without parsing LLM prose.
     */
    richContent?: {
      importCard?: {
        type: string
        data: unknown
      }
      /**
       * PR8.5 Gap B — voice confirmation handshake emitted by the engine's
       * critical-confirmation gate when `VOICE_CRITICAL_CONFIRM_ENFORCE` is
       * enabled. The client parks the parsed handshake in voiceStore so PR10
       * can speak the prompt and replay with the token.
       */
      voiceConfirmation?: VoiceConfirmationRequired
      /**
       * #426 — card-driven equipment-registration flow snapshot. Forwarded
       * verbatim to `chatStore.Message.richContent` so
       * `EquipmentRegistrationCard` renders deterministically. Sidecar.
       */
      equipmentRegistrationFlow?: {
        flowId: string
        state: string
        context: Record<string, unknown>
      }
      /**
       * Fatia 2 da máquina de importação — snapshot do ImportFlow (campos
       * opcionais porque uma recusa pode viajar sem snapshot completo). Passa
       * verbatim ao `chatStore.Message.richContent` e semeia o
       * `importFlowStore` vivo.
       */
      importFlow?: ImportFlowSnapshot
      /**
       * Layer 2 — deterministic read-after-write confirmation of a CRUD/cadastro
       * turn. Emitted by the engine's `buildExecutionConfirmationEnvelope` from
       * the re-read DB ground-truth; passed straight through to
       * `chatStore.Message.richContent` so `ExecutionConfirmationCard` renders
       * without parsing LLM prose. Sidecar — coexists with the text response.
       */
      executionConfirmation?: ExecutionConfirmationEnvelope
      /**
       * Central write→card invariant — persisted-entity card the engine derives
       * from a write tool's `_receipt` (business id + status + fields). Passed
       * straight through to `chatStore.Message.richContent` so the generic
       * `EntityCard` renders the DB read-back. Additive sidecar.
       */
      entityCard?: EntityCard
      /**
       * WS-B4.0 — typed web-search card channel (DA-1). Carries the
       * `ToolOnlyEquipmentCard` verbatim; `useMessageParsing` reuses
       * `adaptToolOnlyToStructured` with zero re-parse of message text. When
       * present, the card renders deterministically and the text is suppressed.
       */
      webSearchResult?: ToolOnlyEquipmentCard
      /**
       * Spec 2026-07-12 (FIX-4) — typed internal-lookup card channel (the
       * "meu cadastro" answer, distinct from `webSearchResult`). Was already
       * defined on `chatStore.Message.richContent` (types/rich-content.ts)
       * but missing from this SSE payload type, so connectionStore never read
       * it and `EquipmentSearchResultCard` never rendered. Passed straight
       * through — never re-parsed from message text.
       */
      readOnlyListCard?: ReadOnlyListCard
      /** FU-193 (0.53.0) — canal plural; o singular é fallback de motor antigo. */
      readOnlyListCards?: ReadOnlyListCard[]
      equipmentSearchResult?: EquipmentSearchResult
      /**
       * WS-B4 — typed dashboard card channel. Carries the engine
       * `analyze_equipment` -> `dashboard_card` envelope verbatim; passed
       * straight through to `chatStore.Message.richContent` so `DashboardCard`
       * renders deterministically (no message-text parse). Sidecar.
       */
      dashboardCard?: DashboardCardEnvelope
      /**
       * WS-B4 — typed preventive-plans card channel. Carries the engine
       * `search_maint_plan` (card mode) -> `plans_card` envelope verbatim so
       * `PlansCard` renders the table + export. Sidecar.
       */
      plansCard?: PlansCardEnvelope
      /**
       * Fase 2 — Camada A. Editable preventive-plan draft emitted by
       * `runPreventivePlanDeterministicTurn` (start/edit/add/delete turns).
       * Carries the full `DraftRow[]` so `PreventivePlanDraftTable` renders
       * deterministically from the engine flow context. Sidecar.
       */
      preventivePlanDraft?: PreventivePlanDraftRichContent
      /**
       * Spec §2.1 / C2 — deterministic 3-option entry card. Forwarded verbatim
       * so `PreventivePlanEntryCard` renders the single day-to-day entry point.
       * Sidecar, sem chave de tela.
       */
      preventivePlanEntryCard?: PreventivePlanEntryCard
      /**
       * Spec §4 / C6 — orquestração-guia card (no team / missing material).
       * Non-blocking sidecar emitted alongside the confirmation.
       */
      guidanceCard?: GuidanceCard
      /**
       * Spec §5.1 / C3 — PLURAL confirmation cards (one per persisted plan).
       * Forwarded verbatim so `MessageBubble` renders one `EntityCard` each
       * (no UUID). Sidecar, sem chave de tela.
       */
      entityCards?: EntityCard[]
      /**
       * Onda 3 / F4 — plan detail card. Emitted by `get_maint_plan` when it
       * finds a plan row; carries header + tasks[] + materials[]. Forwarded
       * verbatim so `PlanDetailCard` renders deterministically. Additive sidecar.
       */
      planDetail?: PlanDetailCardEnvelope
      /**
       * Onda 3 / F4 — work-order detail card. Emitted by `get_work_order` when
       * it finds a WO row; carries header + operations[] + components[]. Forwarded
       * verbatim so `WorkOrderDetailCard` renders deterministically. Additive sidecar.
       */
      workOrderDetail?: WorkOrderDetailCardEnvelope
      symptomPrompt?: import('../types/rich-content').SymptomPromptEnvelope
      /**
       * Trilho finalização 2026-07-15 — plan-edit step sidecar. Carries the
       * editable task rows for the `plan_edit` onboarding step so
       * `PlanEditTable` renders deterministically. Forwarded verbatim.
       */
      planEditTable?: PlanEditTableCard
      /**
       * 2026-08-03 — o card poka-yoke do passo `find_plan`: tipo, marca e
       * modelo com exemplo. Repassado verbatim, como os irmãos.
       */
      equipmentSearchForm?: EquipmentSearchFormCardData
      /** A tela de materiais POR TAREFA (2026-08-03). Repassada verbatim. */
      taskMaterials?: TaskMaterialsCardData
      teamPicker?: import('../types/rich-content').TeamPickerCard
      /**
       * 2026-07-21 — the onboarding rail step identity (spec 01, etapa 0).
       * Passed straight through to `chatStore.Message.richContent` so
       * `ChatPage.tsx`'s kickoff effect can tell a button-less `welcome`
       * render apart from any other rail step, instead of guessing from
       * `suggestedActions` being empty. Sidecar — coexists with `content`.
       */
      onboardingRailStep?: OnboardingRailStepEnvelope
      /**
       * STO-1768 — o motor trocou o canal do cronograma de 52 semanas
       * (commit d9c67b029e, 2026-09-25): antes viajava JSON-stringificado
       * dentro de `response`; agora vem tipado aqui. Passado verbatim para
       * `chatStore.Message.richContent` (ver
       * `RICH_CONTENT_CARD_KEYS` em `stores/connectionStore.ts`) para
       * `MessageBubble` desenhar o `ScheduleCard` sem reparsear texto.
       */
      ltpSchedule?: LtpScheduleEnvelope
      /**
       * STO-1768 — mesma troca de canal, para o `ltp_equipment_picker` que
       * `/cronograma` sem TAG devolve. Ver o comentário de `ltpSchedule` acima.
       */
      ltpEquipmentPicker?: LtpEquipmentPickerEnvelope
    }
  }) => void
  onError: (message: string) => void
  /**
   * Keep-alive comment (`: keep-alive`) received. The engine emits these every
   * ~10s while it holds the connection during a content-silent phase (the
   * deterministic post-loop: completion guard + critic + final-text generation),
   * which carries NO `data:` event. They prove the pipe is alive without being a
   * turn-progress signal, so the handler re-arms the silence watchdogs — a long
   * but alive post-loop must not be killed by the idle watchdog or the hard-cap.
   * Optional: callers that don't track timers can omit it.
   */
  onKeepAlive?: () => void
  /**
   * Axis Insights — o painel FICOU PRONTO (chegou o `artifact_end` e o spec
   * virou JSON). So aqui a tela tem o que desenhar: um painel pela metade nao e
   * um painel incompleto, e um JSON invalido.
   */
  onArtifact?: (artifact: ArtifactPronto) => void
  /**
   * Axis Insights — o painel COMECOU (`artifact_start`): a tela ja sabe titulo,
   * id e versao, e abre a casca com esqueletos no lugar dos blocos. Quando o
   * `artifact_end` chegar, `onArtifact` troca esqueleto por bloco na mesma
   * caixa — sem salto de layout.
   */
  onArtifactStart?: (cabecalho: CabecalhoDeArtifact) => void
  /**
   * Evento de painel fora de ordem, orfao, ou spec ilegivel. Existe porque o
   * `switch` abaixo NAO tem `default`: sem este canal, esses casos voltariam a
   * ser descartados sem erro, sem log e sem sintoma — que foi como os cinco
   * eventos de painel do motor sumiam.
   */
  onArtifactFalhou?: (falha: FalhaDeArtifact) => void
  /** Sugestoes de proxima pergunta que acompanham o painel (STO-29: tambem botões por step/estado). */
  onSuggestions?: (items: Sugestao[]) => void
  /**
   * O CARD, ANTES DO TEXTO CRU.
   *
   * O motor emite `envelope` (`src/routes/chat-stream.ts`) justamente porque o
   * `response` terminal e o UNICO evento que carrega `richContent`, e ele so sai
   * depois que todo o texto ja passou por `chunk`. Sem receber isto aqui, a tela
   * pinta a bolha PELADA e troca no fim — o defeito "texto cru antes do card"
   * que o motor tinha ido consertar, com uma metade so.
   *
   * Medido em 04/09/2026: o motor emitia e este `switch` nao tinha o `case`.
   * Como ele nao tem `default`, o evento sumia sem erro — exatamente como os
   * cinco eventos de painel sumiam, dois comentarios acima.
   *
   * Recebe o `richContent` verbatim. Quem consome NAO liquida o turno: o turno
   * so termina com o `response`.
   */
  onEnvelope?: (richContent: Record<string, unknown>) => void
}

export interface SendChatStreamOptions {
  /** Local authority snapshot; never serialized into the HTTP body. */
  authorizeFetch?: () => 'allowed' | 'denied' | 'stale'
  onStale?: () => void
  /** References to files already uploaded via POST /api/v1/import/upload. */
  fileRefs?: FileRef[]
  /** Legacy inline attachments for types not yet routed through upload. */
  inlineAttachments?: OutboundAttachment[]
  /**
   * Card-149 idempotency token. Same UUID across automatic retries (WiFi
   * blip, page refresh during in-flight request) so the engine can drop
   * the second request and reuse the cached response. Required in
   * practice — connectionStore generates one for every outbound send.
   */
  clientRequestId?: string
  clickedActionLabel?: string
  /**
   * PR8.5 Gap A — origin channel of this turn. The engine's
   * critical-confirmation gate (PR4b, `VOICE_CRITICAL_CONFIRM_ENFORCE`) keys on
   * `commandSource === 'voice'` to require a single-use confirmationToken before
   * a destructive tool (e.g. complete_work_order) executes. A voice-originated
   * turn MUST set this, or the gate is UNREACHABLE — it would silently gate
   * nothing. Threaded verbatim into the /chat/stream body; omitted for normal
   * chat turns so their behaviour is byte-for-byte unchanged (no regression).
   *
   * Feature is DORMANT server-side (`VOICE_CRITICAL_CONFIRM_ENFORCE=false`).
   */
  commandSource?: 'voice' | 'chat'
  /**
   * PR-E — single-use confirmation token. Replayed verbatim from
   * `voiceStore.pendingConfirmation.confirmationToken` so the engine's
   * critical-confirmation gate can consume it deterministically in
   * `complete_work_order` args. Absent ⇒ field is omitted from the body
   * (byte-for-byte unchanged for all non-replay turns).
   */
  confirmationToken?: string
  /**
   * Chip de indicador — id do registro de intents do motor
   * (`modules/insights/intents.ts`). Vai no corpo so quando a pessoa CLICOU um
   * chip; turno digitado nunca o carrega. O motor recusa id desconhecido com
   * 400 UNKNOWN_INTENT, entao ele nunca cai calado.
   */
  intentId?: string
  /**
   * E3 + Trilho finalização 2026-07-15 — deterministic onboarding card action
   * (optional sibling field). Accepts the LEGACY card shape (`{ kind, ... }`)
   * AND the rail-action shape (`{ actionId, payload }`, e.g. `plan_edit.confirm`).
   * SPEC §4.3: this is the SINGLE inbound channel; there is NO parallel
   * `planEditAction`/`railAction` sibling. The engine's edge parser reads
   * `.kind` and `.actionId` off the same envelope and the dispatcher advances
   * the flow by `actionId` when present.
   */
  onboardingAction?: OnboardingActionWire
  /** #426 — card-driven equipment-registration action (optional sibling field). */
  equipmentRegistrationAction?: EquipmentRegistrationActionPayload
  /** #426 C9 — refine/retry search refresh signal (structured sibling field; the
      engine bypasses the AxiomK cache when refresh=true). Sent as a real field (not
      buried in content) so the user bubble stays readable prose AND the engine reads
      a signal it actually parses (body['webSearchRetry']). */
  webSearchRetry?: WebSearchRetryPayload
  /** Fase 2 — Camada A. Deterministic preventive-plan builder action (optional
      sibling field). Mirrors `equipmentRegistrationAction`: the engine runs
      `runPreventivePlanDeterministicTurn` so the turn carries no LLM routing.
      Omitted for all non-preventive turns (body byte-for-byte unchanged). */
  preventivePlanAction?: PreventivePlanAction
  orderExecutionAction?: import('../types/rich-content').OrderExecutionAction
  procedureAuthoringAction?: import('@/features/procedimento-e-foto/procedimento/types').ProcedureAuthoringAction
  kairosAction?: import('@rochadenis/axiomgo-contracts').KairosAction
  /** Consultor spec v4.0 — C9. Structured Consultor→Garçom command (optional sibling
      field). Mirrors `preventivePlanAction`: emitted by a Consultor action-card button,
      threaded verbatim into the /chat/stream body so the engine short-circuits the LLM
      loop (`parseConsultantIntentPayload` → `checkConsultantScope` → deterministic
      dispatch) when `CONSULTANT_INTENT_ENABLED=true`. Omitted for all normal chat turns
      (body byte-for-byte unchanged — flag OFF and absence are indistinguishable). */
  consultantIntent?: ConsultantIntentPayload
  /**
   * B4 fatia 4b — resposta do pulse NPS 0-10 (canal estruturado). Irmão dos
   * demais: sai do card, viaja verbatim no corpo do `/chat/stream` com
   * `content: ''` (a borda do motor sintetiza o rótulo da bolha) e o turno é
   * determinístico — sem laço de LLM, porque a tool `record_nps_response` não
   * está no allowlist de skill nenhuma. Omitido em todo turno que não seja
   * clique do card, então o corpo dos demais é byte-a-byte igual.
   *
   * ⚠️ ESTE CANAL EXIGE O HANDLER DO MOTOR NO MESMO DEPLOY — ele NÃO é inerte
   * sem ele. Sem `summarizeNpsAction` na borda, a guarda de `content`
   * obrigatório responde 400 ao `content: ''`, e como a fatia 4a já emite
   * `npsPulse`, o card apareceria e daria erro no clique — queimando a pesquisa
   * que o motor oferece uma vez na vida do usuário. Contrato §1.3 e §5 em
   * `axiomgo-brain/handoffs/2026-08-06-01-contrato-npsaction-chat-motor.md`.
   */
  npsAction?: NpsAction
  /**
   * Fatia 2 da máquina de importação (spec 2026-07-23 §11) — ação estruturada
   * do card do ImportFlow. Mesma disciplina dos irmãos: `content: ''` (a borda
   * do motor sintetiza a bolha via `summarizeImportFlowAction`) e omitido em
   * todo turno que não seja clique do card.
   */
  importFlowAction?: ImportFlowActionWire
  /**
   * Paleta de comandos do campo de digitar (etapa 1, R10) — o id do comando
   * escolhido na barra ("/equipamento" → `id: 'equipamento'`). Irmão dos
   * demais: omitido em todo turno que não veio de um comando escolhido, para
   * que o corpo continue byte-a-byte igual quando ausente.
   *
   * ⚠️ LACUNA MEDIDA (30/08/2026): o motor ainda não lê este campo —
   * `src/routes/chat-stream.ts` do motor não tem `parseCommandPayload` nem
   * irmão nenhum. Até esse handler existir lá, o motor recebe `command` no
   * corpo e o ignora (não quebra, mas também não roteia deterministicamente
   * por ele) — o turno cai no roteamento normal por LLM usando `content`.
   */
  command?: string
  /**
   * CORR-B F3 — nota de encerramento de operação. Enviada junto com o
   * `confirmationToken` quando `confirmation.noteRequired === true`. Omitida em
   * todo turno sem nota para que o corpo continue byte-a-byte igual.
   */
  closeNote?: string
  /**
   * CORR-B F4b (STO-721) — the wand CLICK, wire-mirror of the engine type
   * `services/closing-note/closing-note-wand-action.ts ClosingNoteWandActionPayload`.
   * Same molde as `npsAction`/`importFlowAction`: `content: ''` (the engine's
   * edge synthesizes the turn label via `summarizeClosingNoteWandAction`) and
   * omitted on every turn that isn't a wand click, so the body stays
   * byte-for-byte identical when absent. The engine short-circuits straight to
   * `enhance_closing_note` (`runClosingNoteWandActionTurn`), never touching the
   * LLM loop.
   */
  closingNoteWandAction?: ClosingNoteWandActionPayload
  /** Deterministic corrective opening; the engine synthesizes the user bubble. */
  correctiveAction?: CorrectiveAction
  notificationAction?: NotificationAction
  manualPlanAction?: import('../types/rich-content').ManualPlanAction
  /** Clique "Assumir como plano oficial" do cartão `planComparison` (índices marcados). */
  planComparisonAction?: PlanComparisonAction
  photoPolicyAction?: PhotoPolicyAction
  agentAction?: AgentAction
}

/** Wire-mirror of the engine's `ClosingNoteWandActionPayload` (see field doc above). */
export interface ClosingNoteWandActionPayload {
  draftText: string
}

/**
 * Consultor spec v4.0 — C9. Discriminated command the senior Consultant emits for the
 * Orchestrator-waiter to execute deterministically. Wire-mirror of the engine type
 * `runtime/consultant-intent.ts ConsultantIntentPayload` (§4.1). The engine re-validates
 * fail-closed (`parseConsultantIntentPayload`) and through the scope-guard fence — the
 * frontend only serializes; it never grants capability.
 */
export type ConsultantIntentKind =
  | 'corrective.open'
  | 'wo.search'
  | 'wo.update'
  | 'wo.complete'
  | 'equip.search'
  | 'equip.register'
  | 'equip.register.guided'
  | 'plan.create'
  | 'kpi.show'
  | 'catalog.research'
  | 'import.spreadsheet'
  | 'general.clarify'

export interface ConsultantIntentPayload {
  kind: ConsultantIntentKind
  /** Validated by the engine against ALLOWED_DOMAINS (default-deny). */
  domain: string
  /** Only primitives survive the engine parse; nested objects are dropped. */
  params: Record<string, string | number | boolean | null>
  /** [P2] Consultant preamble shown before the deterministic card. When absent the
      engine falls back to the deterministic ORCHESTRATION_HINTS phrase. */
  consultantMessage?: string
}

/** #426 C9 — payload for the "Refinar busca" / "Buscar novamente" refresh signal. */
export interface WebSearchRetryPayload {
  type: 'web_search_retry'
  refresh: boolean
  manufacturer?: string
  model?: string
  query?: string
}

/**
 * Send a chat message via SSE streaming endpoint.
 * Returns the AbortController used for the request so the caller can cancel mid-stream.
 * Pass an external `signal` (from a caller-owned AbortController) to delegate
 * cancellation control upward; otherwise an internal controller is created.
 */
export function sendChatStream(
  content: string,
  tenantId: string,
  userId: string,
  conversationId: string | null,
  callbacks: SSECallbacks,
  options: SendChatStreamOptions = {},
  signal?: AbortSignal,
): AbortController {
  const controller = new AbortController()
  const effectiveSignal = signal ?? controller.signal

  const responseIsStale = () => {
    if (options.authorizeFetch?.() !== 'stale') return false
    options.onStale?.()
    return true
  }
  const requireCurrentResponse = (reader: ReadableStreamDefaultReader<Uint8Array>) => {
    if (!responseIsStale()) return
    void reader.cancel().catch(() => undefined)
    throw new DOMException('Stale chat response', 'AbortError')
  }
  const mayFetch = () => {
    const decision = options.authorizeFetch?.() ?? 'allowed'
    if (decision === 'denied') callbacks.onError('errors.permissionDenied')
    if (decision === 'stale') options.onStale?.()
    return decision === 'allowed'
  }

  void (async () => {
    try {
      // DENTRO do bloco de propósito. `getEngineRestUrl()` estoura de verdade
      // quando a variável de build falta numa build de produção
      // (`config.ts`, `missingEnvError`). Avaliado fora daqui, esse estouro
      // subia SÍNCRONO por `sendRaw` — que já tinha acendido o "pensando" e
      // armado os três temporizadores, e não tem `try`/`catch` no caminho. O
      // chamador nem chegava a comparar o retorno do envio: a fileira de
      // botões ficava travada, sem aviso nenhum. Aqui dentro, o mesmo estouro
      // vira `onError`, que é como toda outra falha de envio é reportada.
      const engineUrl = getEngineRestUrl()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId,
        'X-User-Id': userId,
      }
      // O PASSE COM PRAZO — ver `aguardarPasseComPrazo` acima.
      const passe = await aguardarPasseComPrazo(
        () => useAuthStore.getState().getToken(),
        CREDENTIAL_WAIT_TIMEOUT_MS,
      )
      if (!mayFetch()) return
      if (passe.kind === 'timeout') {
        Sentry.captureMessage('credential_wait_timeout', {
          level: 'warning',
          tags: { stream_outcome: 'credential_wait_timeout' },
        })
        callbacks.onError('errors.credentialTimeout')
        return
      }
      if (passe.token) {
        headers['Authorization'] = `Bearer ${passe.token}`
      }

      const body: Record<string, unknown> = {
        content,
        tenantId,
        userId,
        conversationId,
        ...(options.clickedActionLabel ? { clickedActionLabel: options.clickedActionLabel } : {}),
      }
      // Every turn, including empty structured clicks, carries the selected UI locale.
      // If i18next has not resolved yet, the engine uses User.language.
      const turnLocale = currentTurnLocale()
      if (turnLocale) {
        body['locale'] = turnLocale
        // STO-1401/1479 — SÓ manda quando `true`: ausente é o comportamento
        // de sempre (`mirrored`) para clientes antigos; ver
        // `currentTurnLocaleExplicit`.
        if (currentTurnLocaleExplicit()) {
          body['localeExplicit'] = true
        }
      }
      if (options.fileRefs && options.fileRefs.length > 0) {
        body['fileRefs'] = options.fileRefs
      }
      if (options.inlineAttachments && options.inlineAttachments.length > 0) {
        body['attachments'] = options.inlineAttachments
      }
      // Card-149: idempotency token. Backend keys cache by
      // (tenantId, clientRequestId) so a second POST with the same id
      // returns the cached response instead of re-running the agent.
      if (options.clientRequestId) {
        body['clientRequestId'] = options.clientRequestId
      }
      // PR8.5 Gap A: thread commandSource only for voice turns so that normal
      // chat turns are byte-for-byte identical to the pre-voice body. The engine
      // gate keys on this field; omitting it for chat avoids false positives.
      if (options.commandSource) {
        body['commandSource'] = options.commandSource
      }
      // PR-E: replay the single-use confirmation token when present. Absent ⇒
      // field is omitted from the body so all non-replay turns are unchanged.
      if (options.confirmationToken) {
        body['confirmationToken'] = options.confirmationToken
      }
      // CORR-B F3: closing note; omitted when absent so non-note turns are unchanged.
      if (options.closeNote) {
        body['closeNote'] = options.closeNote
      }
      // CORR-B F4b (STO-721): the wand click; omitted on every turn that isn't one.
      if (options.closingNoteWandAction) {
        body['closingNoteWandAction'] = options.closingNoteWandAction
      }
      if (options.correctiveAction) {
        body['correctiveAction'] = options.correctiveAction
      }
      if (options.notificationAction) {
        body['notificationAction'] = options.notificationAction
      }
      if (options.manualPlanAction) {
        body['manualPlanAction'] = options.manualPlanAction
      }
      if (options.agentAction) body['agentAction'] = options.agentAction
      if (options.photoPolicyAction) {
        body['photoPolicyAction'] = options.photoPolicyAction
      }
      if (options.planComparisonAction) {
        body['planComparisonAction'] = options.planComparisonAction
      }
      if (options.intentId) {
        body['intentId'] = options.intentId
      }
      if (options.onboardingAction) {
        body['onboardingAction'] = options.onboardingAction
      }
      if (options.equipmentRegistrationAction) {
        body['equipmentRegistrationAction'] = options.equipmentRegistrationAction
      }
      if (options.webSearchRetry) {
        body['webSearchRetry'] = options.webSearchRetry
      }
      if (options.preventivePlanAction) {
        body['preventivePlanAction'] = options.preventivePlanAction
      }
      if (options.orderExecutionAction) {
        body['orderExecutionAction'] = options.orderExecutionAction
      }
      if (options.procedureAuthoringAction) {
        body['procedureAuthoringAction'] = options.procedureAuthoringAction
      }
      if (options.kairosAction) {
        body['kairosAction'] = options.kairosAction
      }
      // Consultor spec v4.0 (C9): serialize the structured Consultor→Garçom command
      // when present so the engine can short-circuit deterministically. Mirrors
      // preventivePlanAction — omitted for every non-Consultor turn so the body is
      // byte-for-byte unchanged (no regression to the normal chat path).
      if (options.consultantIntent) {
        body['consultantIntent'] = options.consultantIntent
      }
      // B4 fatia 4b — resposta do pulse NPS. Mesma disciplina dos irmãos acima:
      // omitido em todo turno que não seja clique do card, para que o corpo dos
      // demais continue byte-a-byte igual.
      if (options.npsAction) {
        body['npsAction'] = options.npsAction
      }
      // Fatia 2 da máquina de importação — mesma disciplina dos irmãos acima:
      // omitido em todo turno que não seja clique do card do ImportFlow.
      if (options.importFlowAction) {
        body['importFlowAction'] = options.importFlowAction
      }
      // Paleta de comandos (etapa 1, R10) — omitido em todo turno que não
      // veio de um comando escolhido na barra, para que o corpo dos demais
      // continue byte-a-byte igual.
      if (options.command) {
        body['command'] = options.command
      }

      // DIAG-DELAY-1 (a) — o relógio do usuário começa aqui.
      marcaDaTela('a-fetch-disparado', options.clientRequestId)
      if (!mayFetch()) return
      const response = await fetch(`${engineUrl}/api/v1/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: effectiveSignal,
      })

      if (responseIsStale()) {
        await response.body?.cancel().catch(() => undefined)
        return
      }
      if (!response.ok) {
        if (response.status === 401) {
          // R63 — mesmo ponto único do api.ts: renova o passe e mantém a identidade;
          // só desloga quando não dá. Em ambos os casos o balão DIZ o que houve.
          const outcome = await handleUnauthorized()
          callbacks.onError(outcome === 'renewed' ? 'errors.sessionRenewed' : 'errors.authError')
          return
        }
        if (response.status === 403) {
          callbacks.onError('errors.permissionDenied')
          return
        }
        if (response.status === 429) {
          callbacks.onError('errors.rateLimited')
          return
        }
        const errBody = await response.text().catch(() => '')
        if (responseIsStale()) return
        // Engine errors come as JSON `{type:'error', code, message}`. LACUNA
        // #7: never surface the raw envelope OR its raw `message` in the
        // bubble — route it through `displayForServerError` so a mapped
        // business code gets its pt-BR phrase and everything else (including
        // an unparseable body) gets the generic translated message.
        let parsed: { code?: unknown; message?: unknown; details?: unknown } | null = null
        try {
          parsed = JSON.parse(errBody) as typeof parsed
        } catch {
          /* not JSON — no structured envelope to read */
        }
        if (!parsed && errBody) {
          console.error('[chat-stream] raw server error (support log only):', errBody)
        }
        callbacks.onError(displayForServerError(parsed ?? {}))
        return
      }

      if (!response.body) {
        // Client-side condition (fetch resolved `ok`, but with no body) —
        // there is no server text to preserve, just the generic i18n KEY
        // (connectionStore translates keys, not pre-translated text, so this
        // stays consistent with the 401/403/429/network branches above).
        console.error('[chat-stream] response.body is null on an ok response')
        callbacks.onError('errors.streamFailed')
        return
      }

      markSessionHealthy() // R63 — resposta boa: o passe renovado provou que vale
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      // The engine always emits a terminal event (`response`/`error`/`done`) before
      // it ends the socket. If the stream closes without one, the pipe died mid-turn
      // (engine crash / OOM-kill / dropped proxy) — surface it immediately below
      // instead of letting the store wait out the full idle watchdog.
      let sawTerminal = false
      // DIAG-DELAY-1 — uma marca só por turno em cada fronteira.
      let marcouPrimeiroByte = false
      const marcasDoStream = estadoInicialDasMarcas()
      // Um coletor POR STREAM: o estado do painel morre junto com o turno.
      const coletorDePaineis = new ColetorDeArtifacts()

      while (true) {
        const { done, value } = await reader.read()
        requireCurrentResponse(reader)
        if (done) break
        if (!marcouPrimeiroByte) {
          marcouPrimeiroByte = true
          marcaDaTela('b1-primeiro-byte', options.clientRequestId)
        }

        buffer += decoder.decode(value, { stream: true })

        // Process complete SSE events (separated by \n\n)
        const parts = buffer.split('\n\n')
        buffer = parts.pop() ?? ''

        for (const part of parts) {
          requireCurrentResponse(reader)
          // Keep-alive comments (": keep-alive") carry no `data:` event but
          // prove the engine is alive and intentionally holding the connection
          // through a content-silent phase. Surface them as activity so the
          // silence watchdogs are re-armed, then skip parsing.
          if (part.startsWith(':')) {
            callbacks.onKeepAlive?.()
            continue
          }

          for (const line of part.split('\n')) {
            requireCurrentResponse(reader)
            if (!line.startsWith('data: ')) continue

            try {
              const data = JSON.parse(line.slice(6)) as Record<string, unknown>
              const type = data['type'] as string
              // DIAG-DELAY-1 (b2)/(c) — a decisao inteira mora no modulo.
              marcaEventoRecebido(type, options.clientRequestId, marcasDoStream)

              switch (type) {
                case 'processing':
                  callbacks.onProcessing()
                  break
                case 'progress':
                  callbacks.onProgress(
                    data['step'] as string,
                    data['detail'] as string,
                    data['data'],
                  )
                  break
                case 'chunk':
                  callbacks.onChunk(data['content'] as string)
                  break
                case 'response':
                  sawTerminal = true
                  callbacks.onResponse(data as unknown as Parameters<SSECallbacks['onResponse']>[0])
                  break
                case 'error':
                  sawTerminal = true
                  // LACUNA #7: same rule as the non-ok HTTP branch above — a
                  // mapped business `code` gets its pt-BR phrase, anything
                  // else (including the numeric HTTP-status `code` engine
                  // errors always carry) gets the generic translated message.
                  callbacks.onError(
                    displayForServerError({
                      code: data['code'],
                      message: data['message'],
                      details: data['details'],
                    }),
                  )
                  break
                // ── Axis Insights: o painel dentro da conversa ──────────────
                // Estes cinco eventos existiam no motor e caiam no vazio aqui,
                // porque este `switch` nao tem `default`. Agora cada um tem
                // destino, e o que da errado sai por `onArtifactFalhou` em vez
                // de sumir.
                case 'artifact_start': {
                  const cabecalho = data['artifact'] as unknown as CabecalhoDeArtifact
                  coletorDePaineis.start(cabecalho)
                  callbacks.onArtifactStart?.(cabecalho)
                  break
                }
                case 'artifact_delta':
                  entregaFalhaDePainel(
                    callbacks,
                    coletorDePaineis.delta(data['artifactId'] as string, data['patch'] as string),
                  )
                  break
                case 'artifact_end':
                  entregaPainelFechado(
                    callbacks,
                    coletorDePaineis.end(data['artifactId'] as string),
                  )
                  break
                // O card que veste a bolha ANTES do primeiro pedaco de texto.
                // Aditivo: turno que nao emite `envelope` segue igual, e o
                // `response` terminal continua sendo a fonte final da verdade.
                case 'envelope':
                  callbacks.onEnvelope?.(data['richContent'] as Record<string, unknown>)
                  break
                case 'suggestions':
                  callbacks.onSuggestions?.(
                    data['items'] as Array<{ label: string; prompt: string; icon?: string }>,
                  )
                  break
                case 'done':
                  // Stream complete — the engine signalled a clean end.
                  sawTerminal = true
                  break
              }
            } catch {
              // Ignore malformed SSE lines
            }
          }
        }
      }

      // Clean EOF with no terminal event = the pipe died mid-turn. Surface it now
      // (same key as a mid-read failure) so the UI never depends on the idle
      // watchdog to notice a stream that has already ended.
      if (!sawTerminal) {
        // INSTRUMENTAÇÃO (2026-08-31) — o caminho existia e FUNCIONAVA, mas era
        // MUDO: em 31/08 o projeto da tela no Sentry tinha ZERO ocorrências em
        // 24 h com congelamento relatado pelo Flávio, e não havia como
        // distinguir "nunca aconteceu" de "aconteceu e ninguém soube".
        // Caminho de falha sem etiqueta é indistinguível de caminho que nunca
        // falhou. Ver `axiomgo-brain/cortex/patterns.md`, "Término de fluxo".
        //
        // Fica ANTES do onError de propósito: o `onError` do `connectionStore`
        // chama `settleCurrentTurn()` na primeira linha, e daí em diante o vigia
        // de silêncio não dispara mais — ou seja, este bloco e o
        // `stream_idle_timeout` são mutuamente exclusivos no mesmo fluxo.
        // Como ele roda DEPOIS do laço de leitura e sob `!sawTerminal`, executa
        // no máximo uma vez por fluxo, por construção.
        Sentry.captureMessage('stream_ended_without_terminal', {
          level: 'warning',
          tags: { stream_outcome: 'stream_ended_without_terminal' },
        })
        callbacks.onError('errors.streamFailed')
      }
    } catch (err: unknown) {
      if (responseIsStale()) return
      if (effectiveSignal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
        return // Cancelled by caller — not an error
      }
      // Distinguish network-layer failures (CSP block, CORS, DNS, offline,
      // TLS) from stream-mid-read failures so the UI can show a precise,
      // translated message. Browser fetch rejects with `TypeError: Failed
      // to fetch` for the first family on every engine; everything else
      // falls back to a generic stream-failed key. We pass the i18n key
      // (prefixed `errors.`) downstream — `connectionStore.onError`
      // translates it via `i18n.t()` at render time.
      const isNetworkError =
        err instanceof TypeError && /failed to fetch|network|load failed/i.test(err.message ?? '')
      const key = isNetworkError ? 'errors.networkBlocked' : 'errors.streamFailed'
      callbacks.onError(key)
    }
  })()

  return controller
}
